import { useCallback, useEffect, useRef } from 'react';

interface AudioContextOptions {
  audioElementRef: React.RefObject<HTMLAudioElement | null>;
  /**
   * Routing audio through the Web Audio API requires a CORS-enabled source,
   * otherwise the element is silenced. Remote (cross-origin) media is played
   * directly instead.
   */
  enabled?: boolean;
}

/**
 * Create and return an audio context instance for a given audio element [0].
 * Also returns an associated audio source, a gain node, and play and pause actions
 * which should be used instead of `audioElementRef.current.play/pause()`.
 *
 * [0] https://developer.mozilla.org/en-US/docs/Web/API/AudioContext
 */

export const useAudioContext = ({
  audioElementRef,
  enabled = true,
}: AudioContextOptions) => {
  const audioContextRef = useRef<AudioContext>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode>(null);
  const gainNodeRef = useRef<GainNode>(null);

  useEffect(() => {
    if (
      !enabled ||
      !audioElementRef.current ||
      typeof AudioContext === 'undefined'
    ) {
      return;
    }

    const context = audioContextRef.current ?? new AudioContext();
    const source =
      sourceRef.current ??
      context.createMediaElementSource(audioElementRef.current);

    const gainNode = context.createGain();
    gainNode.connect(context.destination);
    source.connect(gainNode);

    audioContextRef.current = context;
    gainNodeRef.current = gainNode;
    sourceRef.current = source;

    return () => {
      if (context.state !== 'closed') {
        void context.close();
      }
    };
  }, [audioElementRef, enabled]);

  const playAudio = useCallback(() => {
    if (!audioElementRef.current) return;

    void audioElementRef.current.play();
    void audioContextRef.current?.resume();
  }, [audioElementRef]);

  const pauseAudio = useCallback(() => {
    if (!audioElementRef.current) return;

    audioElementRef.current.pause();
    void audioContextRef.current?.suspend();
  }, [audioElementRef]);

  return {
    audioContextRef,
    sourceRef,
    gainNodeRef,
    playAudio,
    pauseAudio,
  };
};
